import React, { useState, useEffect, useRef } from "react";
import './PagesCss/About.css';
import { useNavigate } from "react-router-dom";

/* ── intersection reveal hook ── */
function useReveal() {
  const [seen, setSeen] = useState(new Set());
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) setSeen((p) => new Set([...p, e.target.dataset.key]));
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll("[data-key]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return (k) => seen.has(k);
}

/* ── animated counter ── */
function Counter({ to, suffix = "" }) {
  const [n, setN] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        io.disconnect();
        let start = 0;
        const step = Math.ceil(to / 60);
        const id = setInterval(() => {
          start = Math.min(start + step, to);
          setN(start);
          if (start >= to) clearInterval(id);
        }, 18);
      }
    }, { threshold: 0.5 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [to]);
  return <span ref={ref}>{n}{suffix}</span>;
}

/* ── timeline data ── */
const TIMELINE = [
  {
    year: "2025",
    title: "The Idea",
    body: "D-Tech Studios was born from a simple frustration too many repetitive tasks, too few tools built with care. The founder decided to stop waiting and start building.",
  },
  {
    year: "2025",
    title: "First Products",
    body: "The first in-house tools launched: AfterTaxPay, Font-Max, and QuickPDFs. Each one built from scratch, fully owned, and designed around real user problems.",
  },
  {
    year: "2026",
    title: "Expanding the Portfolio",
    body: "The studio scaled to 10 products across six verticals AI, finance, privacy, voice, crypto, and productivity. Every product conceived, designed, and shipped in-house.",
  },
  {
    year: "2026",
    title: "Building the Platform",
    body: "D-Tech Studios began laying the groundwork for a unified AI-powered platform interconnected tools, shared infrastructure, and developer APIs. The mission is just getting started.",
  },
];

/* ── values data ── */
const VALUES = [
  {
    n: "01",
    title: "Full Ownership",
    body: "Every product is conceived, designed, and developed entirely in-house. We take complete responsibility from the first wireframe to the final deployment.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ab-val-svg">
        <rect x="3" y="11" width="18" height="11" rx="3" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        <circle cx="12" cy="16" r="1.5" fill="currentColor"/>
      </svg>
    ),
  },
  {
    n: "02",
    title: "Eliminate Friction",
    body: "We identify repetitive and time-consuming tasks and remove them with intelligent, seamless solutions. If something can be automated cleanly, it will be.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ab-val-svg">
        <path d="M13 2L4.5 13.5H11L10 22L19.5 10.5H13L13 2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    n: "03",
    title: "Transparent by Design",
    body: "Users know exactly who created our software and why. Accountability and clarity are not features they are the foundation everything else is built on.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ab-val-svg">
        <path d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z" stroke="currentColor" strokeWidth="1.6"/>
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6"/>
      </svg>
    ),
  },
  {
    n: "04",
    title: "Long-Term Thinking",
    body: "Launching is step one. We maintain, evolve, and improve every product we ship. Our roadmap stretches years ahead not just to the next release.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ab-val-svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    n: "05",
    title: "Radical Simplicity",
    body: "The hardest thing to build is something simple. We relentlessly trim complexity until a product does exactly what it needs to and nothing more.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ab-val-svg">
        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    n: "06",
    title: "Built for Everyone",
    body: "Not just enterprises, not just developers. Every product we build should be accessible to a student, a freelancer, a founder, or a Fortune 500 team without a manual.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ab-val-svg">
        <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M3 20c0-3.31 2.69-6 6-6s6 2.69 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        <circle cx="17" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M21 20c0-2.76-1.79-5-4-5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
  },
];

/* ── focus areas ── */
const FOCUS = [
  { label: "AI & Automation",        n: "3 products" },
  { label: "Finance & Tax",          n: "2 products" },
  { label: "Privacy & Email",        n: "1 product"  },
  { label: "Voice & Audio AI",       n: "1 product"  },
  { label: "Design & Typography",    n: "2 products" },
  { label: "Crypto & Web3",          n: "1 product"  },
];

/* ══════════════════════════════════════════════════
   FOUNDER CARD
══════════════════════════════════════════════════ */
function FounderCard() {
  const navigate = useNavigate();
  return (
    <div className="ab-founder-card">

      {/* ── LEFT: image column ── */}
      <div className="ab-founder-img-col">
        <div className="ab-founder-circle-wrap">
          <img
            src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEjUtbqBobN4nKfxCqgX8g9C8ddFFTITWgEsv-BWaOYAAOx4nXhkzIqT5T2dIXGf2UxERHcUvj5li2afdTH2jPurHsBfRdu7QD5DSi0d6oIrjYslSdxP9nEo5QpSAPAYkrDrSeu2G4ZCJrgPFx0arWn7Dkh3Y179jVE-vJsXBdP_r1jxXkM/s1600/Darshankumar-Dhanani.jpg"
            alt="Darshankumar Dhanani Founder, D-Tech Studios"
            className="ab-founder-img-circle"
          />
        </div>
        <div className="ab-founder-badge">Founder</div>
        <h3 className="ab-founder-name">Darshankumar Dhanani</h3>
        <p className="ab-founder-role">Founder &amp; Lead Developer</p>
        <div className="ab-founder-links-col">
          <button className="ab-founder-link-dark"
          onClick={() => navigate("/Products")}
          >
            View Portfolio <span className="ab-btn-arr">→</span>
          </button>
          <button className="ab-founder-link-bare"
          onClick={() => navigate("/FounderBio")}
          >
            Meet the Founder
          </button>
          <a href="https://www.linkedin.com/in/darshan-dhanani-bba35b251/" target="_blank" rel="noopener noreferrer" className="ab-founder-link-bare">
            Connect on LinkedIn
          </a>
        </div>
      </div>

      {/* ── RIGHT: info + highlights ── */}
      <div className="ab-founder-info">

        <p className="ab-founder-bio">
  Darshankumar is a versatile tech and finance professional with expertise in UI/UX, frontend and mobile app development, crypto, and project management. He founded D-Tech Studios to create innovative, high-quality products that solve real-world problems. Each product in the D-Tech portfolio reflects his hands-on approach, combining technology, finance insights, and marketing know-how to deliver solutions with impact and excellent user experience.
</p>

        {/* skill bars */}
        <div className="ab-founder-skills">
          {[
            { label: "AI & Automation",      pct: 95 },
            { label: "Full-Stack Development", pct: 92 },
            { label: "Product Design",        pct: 85 },
            { label: "Privacy & Security",    pct: 80 },
          ].map((s, i) => (
            <div className="ab-skill-row" key={i}>
              <div className="ab-skill-meta">
                <span className="ab-skill-label">{s.label}</span>
                <span className="ab-skill-pct">{s.pct}%</span>
              </div>
              <div className="ab-skill-track">
                <div className="ab-skill-fill" style={{ width: `${s.pct}%` }} />
              </div>
            </div>
          ))}
        </div>

        {/* mini stat pills */}
        <div className="ab-founder-stats">
          {[
            { n: "10+",  l: "Products Shipped" },
            { n: "3+",   l: "Years Building" },
            { n: "100%", l: "In-house Work" },
            { n: "6",    l: "Verticals" },
          ].map((s, i) => (
            <div className="ab-founder-stat" key={i}>
              <span className="ab-founder-stat-n">{s.n}</span>
              <span className="ab-founder-stat-l">{s.l}</span>
            </div>
          ))}
        </div>

        {/* tags */}
        <div className="ab-founder-tags">
          {["AI & Automation", "Full-Stack Dev", "SaaS", "Product Design", "Privacy Tech"].map((t, i) => (
            <span className="ab-founder-tag" key={i}>{t}</span>
          ))}
        </div>

      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   PAGE COMPONENT
══════════════════════════════════════════════════ */
export default function About() {
  const vis = useReveal();
  const navigate = useNavigate();

  return (
    <div className="ab-root">

      {/* ══ HERO ══ */}
      <section className="ab-hero">
        <div className="ab-hero-inner">
          <div className="ab-hero-l">
            <p className="ab-eyebrow">
              <span className="ab-eyebrow-line" />
              About D-Tech Studios
            </p>
            <h1 className="ab-h1">
              One studio.<br />
              One mission.<br />
              <span className="ab-h1-muted">No outsourcing.</span>
            </h1>
            <p className="ab-hero-body">
              D-Tech Studios is an independent AI and SaaS studio that builds tools people
              actually reach for. Every product is conceived, designed, and developed
              entirely in-house reflecting our belief that real quality comes from full
              ownership and deep accountability.
            </p>
          </div>

          {/* right: decorative grid with stats */}
          <div className="ab-hero-r">
            <div className="ab-hero-grid">
              <div className="ab-hg-card ab-hg-card--dark">
                <span className="ab-hg-n"><Counter to={10} /></span>
                <span className="ab-hg-l">Products Built</span>
              </div>
              <div className="ab-hg-card">
                <span className="ab-hg-n ab-hg-n--ink"><Counter to={5} suffix="+" /></span>
                <span className="ab-hg-l ab-hg-l--ink">Live Today</span>
              </div>
              <div className="ab-hg-card">
                <span className="ab-hg-n ab-hg-n--ink"><Counter to={6} /></span>
                <span className="ab-hg-l ab-hg-l--ink">Verticals</span>
              </div>
              <div className="ab-hg-card ab-hg-card--surface">
                <span className="ab-hg-n ab-hg-n--ink">100%</span>
                <span className="ab-hg-l ab-hg-l--ink">In-house</span>
              </div>

              {/* big quote card */}
              <div className="ab-hg-card ab-hg-card--quote">
                <p className="ab-hg-quote">
                  "Automation isn't about replacing humans. It's about giving them back their time."
                </p>
                <span className="ab-hg-attr">D-Tech Studios</span>
              </div>

              {/* year badge */}
              <div className="ab-hg-card ab-hg-card--year">
                <span className="ab-hg-year">Since</span>
                <span className="ab-hg-year-n">2025</span>
              </div>
            </div>
          </div>
        </div>

        {/* scrolling word strip */}
        <div className="ab-marquee">
          <div className="ab-marquee-track">
            {["Our Story", "Who We Are", "The Mission", "Built In-House", "Full Ownership", "Independent Studio"].concat(
              ["Our Story", "Who We Are", "The Mission", "Built In-House", "Full Ownership", "Independent Studio"]
            ).map((t, i) => (
              <span key={i} className="ab-marquee-item">
                {t}<span className="ab-marquee-sep">·</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHO WE ARE ══ */}
      <section
        className={`ab-section ab-who ${vis("who") ? "ab-in" : ""}`}
        data-key="who"
      >
        <div className="ab-who-inner">
          <div className="ab-who-l">
            <p className="ab-tag">Who we are</p>
            <h2 className="ab-h2">
              An independent studio<br />
              <em>that ships real products.</em>
            </h2>
          </div>
          <div className="ab-who-r">
            <p>
              D-Tech Studios is not an agency. We don't take client work, we don't outsource,
              and we don't build throwaway prototypes. We are an independent product studio
              every tool we release is something we believe in, maintain, and stand behind with
              our name.
            </p>
            <p>
              Our focus is the intersection of AI, automation, and productivity. We build software
              that removes friction, streamlines workflows, and gives people back the one resource
              that can never be recovered time. Each product reflects our core belief: that great
              software is built by people who care deeply about the problem they're solving.
            </p>
            <p>
              From privacy-first email tools to voice AI, finance calculators to typography
              platforms every product in our portfolio was born from a genuine gap in the
              market, not a trend.
            </p>
          </div>
        </div>
      </section>

      {/* ══ STATS BAND ══ */}
      <section
        className={`ab-stats ${vis("stats") ? "ab-in" : ""}`}
        data-key="stats"
      >
        <div className="ab-stats-grid">
          {[
            { n: 10,  s: "",   label: "Products Built" },
            { n: 100, s: "K+", label: "Tasks automated monthly" },
            { n: 6,   s: "",   label: "Verticals covered" },
            { n: 100, s: "%",  label: "In-house. Always." },
          ].map((s, i) => (
            <div className="ab-stat" key={i}>
              <strong className="ab-stat-n">
                <Counter to={s.n} suffix={s.s} />
              </strong>
              <span className="ab-stat-l">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══ FOUNDER ══ */}
      <section
        className={`ab-section ab-founder ${vis("founder") ? "ab-in" : ""}`}
        data-key="founder"
      >
        <div className="ab-founder-head">
          <p className="ab-tag">The person behind it</p>
          <h2 className="ab-h2">Meet the founder.</h2>
        </div>
        <div className="ab-founder-body">
          <FounderCard />
        </div>
      </section>

      {/* ══ VALUES ══ */}
      <section
        className={`ab-section ab-values ${vis("values") ? "ab-in" : ""}`}
        data-key="values"
      >
        <div className="ab-values-head">
          <p className="ab-tag">What we stand for</p>
          <h2 className="ab-h2">Six principles we<br /><em>build everything by.</em></h2>
        </div>
        <div className="ab-values-grid">
          {VALUES.map((v, i) => (
            <div
              className="ab-val-card"
              key={i}
              style={{ animationDelay: `${i * 0.07}s` }}
            >
              <div className="ab-val-top">
                <span className="ab-val-n">{v.n}</span>
                <div className="ab-val-icon">{v.icon}</div>
              </div>
              <h3 className="ab-val-title">{v.title}</h3>
              <p className="ab-val-body">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══ TIMELINE ══ */}
      <section
        className={`ab-section ab-timeline ${vis("timeline") ? "ab-in" : ""}`}
        data-key="timeline"
      >
        <div className="ab-timeline-head">
          <p className="ab-tag">Our journey</p>
          <h2 className="ab-h2">How we got here.</h2>
        </div>
        <div className="ab-timeline-list">
          {TIMELINE.map((t, i) => (
            <div className="ab-tl-item" key={i} style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="ab-tl-year-col">
                <span className="ab-tl-year">{t.year}</span>
                <div className="ab-tl-dot" />
              </div>
              <div className="ab-tl-content">
                <h3 className="ab-tl-title">{t.title}</h3>
                <p className="ab-tl-body">{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ FOCUS AREAS ══ */}
      <section
        className={`ab-section ab-focus ${vis("focus") ? "ab-in" : ""}`}
        data-key="focus"
      >
        <div className="ab-focus-head">
          <p className="ab-tag">What we build</p>
          <h2 className="ab-h2">Six verticals.<br /><em>One studio.</em></h2>
        </div>
        <div className="ab-focus-grid">
          {FOCUS.map((f, i) => (
            <div className="ab-focus-card" key={i} style={{ animationDelay: `${i * 0.06}s` }}>
              <span className="ab-focus-n">{String(i + 1).padStart(2, "0")}</span>
              <span className="ab-focus-label">{f.label}</span>
              <span className="ab-focus-count">{f.n}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section
        className={`ab-section ab-cta ${vis("cta") ? "ab-in" : ""}`}
        data-key="cta"
      >
        <div className="ab-cta-inner">
          <div className="ab-cta-mark">D</div>
          <div className="ab-cta-copy">
            <h2 className="ab-cta-h">
              Every product<br />bears our name.
            </h2>
            <p>
              All D-Tech Studios products are built entirely in-house. No outsourcing, no hidden
              processes just a team fully accountable for its work.
            </p>
            <div className="ab-cta-btns">
              <button className="ab-btn-dark"
              onClick={() => navigate("/Products")}
              >
                See Our Products <span className="ab-btn-arr">→</span>
              </button>
              <button className="ab-btn-bare"
              onClick={() => navigate("/Contact")}
              >Get In Touch</button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}